import { ScoringStandardDetail } from '@standard/models/scoring-standard.model';
import { useEffect, useMemo, useRef } from 'react';
import useScoringStandardDetailQuery from '../queries/use-scoring-standard-detail-query.hook';
import useStandardDetailLayoutParams from './use-standard-detail-layout-params.hook';
import useStandardSearchState from './use-standard-search-state.hook';

export default function useStandardDetailDataState() {
  // #region Dependencies
  const standardDetailLayoutParams = useStandardDetailLayoutParams();
  const standardSearchState = useStandardSearchState();
  // #endregion

  // #region Query
  const scoringStandardDetail = useScoringStandardDetailQuery({
    partId: standardDetailLayoutParams.params?.partId,
    subjectType: standardSearchState.state?.subjectId,
  });
  // #endregion

  // #region Effects
  const handleNavigateSearchRef = useRef(standardSearchState.handleNavigate);
  handleNavigateSearchRef.current = standardSearchState.handleNavigate;
  useEffect(() => {
    const searchState = standardSearchState.state;
    let shouldChangeSearch = false;
    if (
      searchState?.subjectId != null &&
      scoringStandardDetail.data?.subjectId != null &&
      searchState.subjectId !== scoringStandardDetail.data.subjectId
    ) {
      searchState.subjectId = scoringStandardDetail.data.subjectId;
      shouldChangeSearch = true;
    }

    if (
      searchState?.universityId != null &&
      scoringStandardDetail.data?.universityId != null &&
      searchState.universityId !== scoringStandardDetail.data.universityId
    ) {
      searchState.universityId = scoringStandardDetail.data.universityId;
      shouldChangeSearch = true;
    }

    if (
      searchState?.bunriId != null &&
      scoringStandardDetail.data?.bunriId != null &&
      searchState.bunriId !== scoringStandardDetail.data.bunriId
    ) {
      searchState.bunriId = scoringStandardDetail.data.bunriId;
      shouldChangeSearch = true;
    }

    if (
      searchState?.facultyId != null &&
      scoringStandardDetail.data?.facultyId != null &&
      searchState.facultyId !== scoringStandardDetail.data.facultyId
    ) {
      searchState.facultyId = scoringStandardDetail.data.facultyId;
      shouldChangeSearch = true;
    }

    if (!shouldChangeSearch || searchState == null) return;
    handleNavigateSearchRef.current(searchState, { replace: true });
  }, [scoringStandardDetail.data, standardSearchState.state]);
  // #endregion

  // #region Computed Values
  const data = useMemo<ScoringStandardDetail | undefined>(() => {
    const defaultValue = {
      bunriId: standardSearchState.state?.bunriId,
      bunriName: standardSearchState.state?.bunriName,
      facultyId: standardSearchState.state?.facultyId,
      facultyName: standardSearchState.state?.facultyName,
      maxScore: 0,
      partId: standardDetailLayoutParams.params?.partId ?? '',
      partNumber: standardSearchState.state?.partNumber ?? null,
      partTitle: standardSearchState.state?.partTitle ?? '',
      partYear: standardSearchState.state?.partYear ?? null,
      questionDetails: [],
      subjectId: standardSearchState.state?.subjectId,
      subjectName: standardSearchState.state?.subjectName ?? '',
      universityId: standardSearchState.state?.universityId,
      universityName: standardSearchState.state?.universityName ?? '',
      updatedAt: null,
      updatedBy: null,
    };
    if (scoringStandardDetail.data) return { ...defaultValue, ...scoringStandardDetail.data };

    return defaultValue;
  }, [
    scoringStandardDetail.data,
    standardDetailLayoutParams.params?.partId,
    standardSearchState.state?.bunriId,
    standardSearchState.state?.bunriName,
    standardSearchState.state?.facultyId,
    standardSearchState.state?.facultyName,
    standardSearchState.state?.partNumber,
    standardSearchState.state?.partTitle,
    standardSearchState.state?.partYear,
    standardSearchState.state?.subjectId,
    standardSearchState.state?.subjectName,
    standardSearchState.state?.universityId,
    standardSearchState.state?.universityName,
  ]);
  // #endregion

  return {
    scoringStandardDetail: { ...scoringStandardDetail, data },
    standardDetailLayoutParams,
    standardSearchState,
  };
}
